import { useEffect, useState } from "react";
import api from "@/lib/api";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import {
  Dialog,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { Info, ClipboardList, Eye } from "lucide-react";
import HiddenTipHint from "@/components/HiddenTipHint";
import { pad, wdLabel, PERIOD_STATUS_LABELS } from "@/lib/yasamaru";

function statusCls(label) {
  if (label.startsWith("あと") || label.endsWith("入れたい")) return "bg-yellow-50 text-yellow-700 border-yellow-200";
  if (label.endsWith("日多い")) return "bg-orange-50 text-orange-700 border-orange-200";
  const map = {
    "希望通り": "bg-green-50 text-green-700 border-green-200",
    "希望に近い": "bg-yellow-50 text-yellow-700 border-yellow-200",
    "希望超過": "bg-red-50 text-red-700 border-red-200",
    "前回少なかったので優先候補": "bg-blue-50 text-blue-700 border-blue-200",
    "どちらでもいい日から多めに採用": "bg-purple-50 text-purple-700 border-purple-200",
  };
  return map[label] || "bg-slate-100 text-slate-700 border-slate-200";
}

function Badge({ children, cls }) {
  return (
    <span className={`text-xs font-semibold border rounded-full px-2 py-0.5 whitespace-nowrap ${cls}`}>
      {children}
    </span>
  );
}

/** props: { embeddedPeriodId?: string, hideTitle?: boolean } */
export default function StaffStatus({ embeddedPeriodId, hideTitle = false } = {}) {
  const [periods, setPeriods] = useState([]);
  const [periodId, setPeriodId] = useState(embeddedPeriodId || "");
  const [rows, setRows] = useState([]);
  const [detailUser, setDetailUser] = useState(null);
  const [period, setPeriod] = useState(null);
  const [requests, setRequests] = useState([]);
  const [shifts, setShifts] = useState([]);

  // load periods list if standalone
  useEffect(() => {
    if (embeddedPeriodId) return;
    api.get("/shift-periods").then((r) => {
      setPeriods(r.data);
      if (r.data[0]) setPeriodId(r.data[0].id);
    });
  }, [embeddedPeriodId]);

  // keep periodId synced with embedded prop
  useEffect(() => { if (embeddedPeriodId) setPeriodId(embeddedPeriodId); }, [embeddedPeriodId]);

  useEffect(() => {
    if (!periodId) return;
    api.get(`/shifts/staff-status/${periodId}`).then((r) => setRows(r.data));
    api.get(`/shift-periods/${periodId}`).then((r) => setPeriod(r.data)).catch(() => {});
    api.get(`/shift-requests?shift_period_id=${periodId}`).then((r) => setRequests(r.data || [])).catch(() => setRequests([]));
    api.get(`/shifts?shift_period_id=${periodId}`).then((r) => setShifts(r.data || [])).catch(() => setShifts([]));
  }, [periodId]);

  const days = period ? new Date(period.year, period.month, 0).getDate() : 0;
  const closedSet = new Set(period?.closed_dates || []);

  return (
    <div className="space-y-4" data-testid="staff-status-page">
      {!hideTitle && (
        <div className="flex items-center justify-between flex-wrap gap-2">
          <div>
            <h1 className="text-2xl md:text-3xl font-bold tracking-tight text-slate-900">スタッフ状況一覧</h1>
            <p className="text-sm text-slate-500 mt-0.5">希望提出と配置のバランスを一覧</p>
            <div className="mt-2 flex flex-wrap gap-1.5">
              <HiddenTipHint text="希望と配置のバランスをひと目で" anchor="tip-status" />
              <HiddenTipHint text="希望の叶い具合をチェック" anchor="tip-fulfillment" />
            </div>
          </div>
          {!embeddedPeriodId && (
            <Select value={periodId} onValueChange={setPeriodId}>
              <SelectTrigger className="w-56 bg-white" data-testid="staff-status-period"><SelectValue /></SelectTrigger>
              <SelectContent>
                {periods.map((p) => (
                  <SelectItem key={p.id} value={p.id}>{p.year}年{p.month}月分 ({PERIOD_STATUS_LABELS[p.status] || p.status})</SelectItem>
                ))}
              </SelectContent>
            </Select>
          )}
        </div>
      )}

      <Card className="p-3 bg-blue-50 border border-blue-200">
        <div className="flex gap-2 items-start text-xs text-slate-700">
          <Info className="w-4 h-4 text-blue-600 shrink-0 mt-0.5" />
          <div className="space-y-0.5">
            <p><b>◎ △ ×</b>: スタッフが提出した希望内訳 (入れます / 必要なら / 入れません の日数)</p>
            <p><b>目標日数</b>: スタッフ本人が「今月X日働きたい」と設定した目標</p>
            <p><b>入っている</b>: 現在シフトに入っている日数 (仮シフト + 確定シフトの合計)</p>
            <p><b>目標まで</b> = 目標日数 − 入っている (プラス = まだ余裕、マイナス = 超過)</p>
            <p><b>未採用</b> = ◎で出してくれたのに、まだ入れられていない日数</p>
            <p className="text-blue-700">右端「日ごとを見る」をクリックすると、その人の希望と配置を日別に確認できます。</p>
          </div>
        </div>
      </Card>

      <Card className="bg-white border border-slate-200 overflow-x-auto">
        <table className="w-full text-sm">
          <thead>
            <tr className="bg-slate-50 border-b border-slate-200 text-left text-xs text-slate-600 uppercase tracking-wider">
              <th className="px-3 py-2.5">氏名</th>
              <th className="px-3 py-2.5 text-center">◎ △ ×</th>
              <th className="px-3 py-2.5 text-center">目標</th>
              <th className="px-3 py-2.5 text-center">入っている</th>
              <th className="px-3 py-2.5 text-center">目標まで</th>
              <th className="px-3 py-2.5 text-center">未採用</th>
              <th className="px-3 py-2.5 text-center">前月差</th>
              <th className="px-3 py-2.5">状態</th>
              <th className="px-3 py-2.5 text-center"></th>
            </tr>
          </thead>
          <tbody>
            {rows.map((r) => {
              const goalLeft = r.days_until_max;
              return (
                <tr key={r.user_id} className="border-b border-slate-100 hover:bg-slate-50" data-testid={`status-row-${r.user_id}`}>
                  <td className="px-3 py-2.5 font-semibold whitespace-nowrap">
                    {r.display_name || r.name}
                    {r.has_late_submission && (
                      <span className="ml-1.5 text-[10px] font-bold bg-orange-100 text-orange-800 border border-orange-300 rounded px-1.5 py-0.5 align-middle" title="締切後の遅れ提出が含まれます">遅れ</span>
                    )}
                  </td>
                  <td className="px-3 py-2.5 text-center text-xs whitespace-nowrap">
                    <span className="text-emerald-700 font-semibold">{r.requested_days}</span>
                    <span className="text-slate-400"> / </span>
                    <span className="text-amber-700">{r.either_days}</span>
                    <span className="text-slate-400"> / </span>
                    <span className="text-rose-700">{r.unavailable_days}</span>
                  </td>
                  <td className="px-3 py-2.5 text-center text-slate-700">{r.monthly_desired_days ?? "-"}</td>
                  <td className="px-3 py-2.5 text-center font-bold text-blue-700">{r.total_shift_days}</td>
                  <td className={`px-3 py-2.5 text-center font-semibold ${
                    goalLeft === null ? "text-slate-400" :
                    goalLeft < 0 ? "text-red-700" :
                    goalLeft === 0 ? "text-green-700" :
                    goalLeft <= 1 ? "text-yellow-700" : "text-slate-700"
                  }`}>
                    {goalLeft === null ? "-" : (goalLeft > 0 ? `+${goalLeft}日` : `${goalLeft}日`)}
                  </td>
                  <td className={`px-3 py-2.5 text-center ${r.unscheduled_request_count >= 5 ? "text-red-700 font-semibold" : r.unscheduled_request_count > 0 ? "text-orange-700" : "text-slate-400"}`}>
                    {r.unscheduled_request_count}
                  </td>
                  <td className={`px-3 py-2.5 text-center ${
                    r.prev_diff_vs_desired === null ? "text-slate-400" :
                    r.prev_diff_vs_desired < 0 ? "text-red-700 font-semibold" : "text-slate-500"
                  }`}>
                    {r.prev_diff_vs_desired === null ? "-" : (r.prev_diff_vs_desired > 0 ? `+${r.prev_diff_vs_desired}` : r.prev_diff_vs_desired)}
                  </td>
                  <td className="px-3 py-2.5">
                    <div className="flex gap-1 flex-wrap">
                      {(r.status_labels || []).map((l, i) => (
                        <Badge key={i} cls={statusCls(l)}>{l}</Badge>
                      ))}
                      {(r.status_labels || []).length === 0 && r.monthly_desired_days === null && (
                        <span className="text-xs text-slate-400">目標未設定</span>
                      )}
                    </div>
                  </td>
                  <td className="px-3 py-2.5 text-center">
                    <Button
                      variant="outline"
                      size="sm"
                      className="h-7 text-xs whitespace-nowrap"
                      onClick={() => setDetailUser(r)}
                      data-testid={`view-day-${r.user_id}`}
                    >
                      <Eye className="w-3 h-3 mr-1" />日ごとを見る
                    </Button>
                  </td>
                </tr>
              );
            })}
            {rows.length === 0 && (
              <tr><td colSpan={9} className="py-6 text-center text-sm text-slate-400">データがありません</td></tr>
            )}
          </tbody>
        </table>
      </Card>

      <p className="text-xs text-slate-500 px-1">
        ※ 「未採用」が多い人は、まだ入れる余地のある人。優先候補としてご検討ください。「前月差」がマイナスなら、前月より少ないシフト数になっています。
      </p>

      {/* 日ごとを見る ダイアログ */}
      <DayByDayDialog
        user={detailUser}
        period={period}
        days={days}
        closedSet={closedSet}
        requests={requests}
        shifts={shifts}
        onClose={() => setDetailUser(null)}
      />
    </div>
  );
}

function DayByDayDialog({ user, period, days, closedSet, requests, shifts, onClose }) {
  if (!user || !period) return null;
  const reqByDate = {};
  for (const r of requests) {
    if (r.user_id === user.user_id) reqByDate[r.date] = r;
  }
  const shiftByDate = {};
  for (const s of shifts) {
    if (s.user_id === user.user_id) shiftByDate[s.date] = s;
  }

  const sym = (t) => t === "available" ? "◎ 入れます" : t === "either" ? "△ 必要なら" : t === "unavailable" ? "× 入れません" : "未提出";
  const reqTone = (t) => t === "available" ? "text-emerald-700 bg-emerald-50 border-emerald-200"
                      : t === "either" ? "text-amber-700 bg-amber-50 border-amber-200"
                      : t === "unavailable" ? "text-rose-700 bg-rose-50 border-rose-200"
                      : "text-slate-500 bg-slate-50 border-slate-200";

  const dayList = [];
  for (let d = 1; d <= days; d++) {
    const dateStr = `${period.year}-${pad(period.month)}-${pad(d)}`;
    const wd = new Date(period.year, period.month - 1, d).getDay();
    dayList.push({
      d, wd, dateStr,
      isClosed: closedSet.has(dateStr),
      r: reqByDate[dateStr],
      s: shiftByDate[dateStr],
    });
  }

  return (
    <Dialog open={!!user} onOpenChange={(v) => !v && onClose()}>
      <DialogContent className="max-w-lg max-h-[85vh] overflow-y-auto" data-testid="day-by-day-dialog">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <ClipboardList className="w-5 h-5 text-blue-600" />{user.display_name || user.name} さんの希望 / 配置
          </DialogTitle>
        </DialogHeader>
        <div className="text-xs text-slate-600 mb-2 flex flex-wrap gap-3">
          <span>◎ <b className="text-emerald-700">{user.requested_days}</b>日</span>
          <span>△ <b className="text-amber-700">{user.either_days}</b>日</span>
          <span>× <b className="text-rose-700">{user.unavailable_days}</b>日</span>
          <span className="ml-auto text-blue-700">配置済み <b>{user.total_shift_days}</b>日</span>
        </div>
        <ul className="divide-y divide-slate-100 border border-slate-200 rounded">
          {dayList.map(({ d, wd, isClosed, r, s }) => (
            <li key={d} className={`px-3 py-1.5 grid grid-cols-[60px_1fr_1fr] items-center gap-2 text-sm ${isClosed ? "bg-slate-50" : ""}`}>
              {/* date */}
              <div className="flex items-center gap-1">
                <span className={`font-bold ${wd === 0 ? "text-red-500" : wd === 6 ? "text-blue-500" : "text-slate-700"}`}>{d}日</span>
                <span className="text-[10px] text-slate-400">({wdLabel(wd)})</span>
              </div>
              {/* 希望 */}
              <div>
                {isClosed ? (
                  <span className="text-[10px] bg-slate-200 text-slate-600 px-1.5 py-0.5 rounded">休業</span>
                ) : (
                  <div className="flex items-center gap-1.5 flex-wrap">
                    <span className={`text-[11px] px-1.5 py-0.5 rounded border font-semibold ${reqTone(r?.request_type)}`}>
                      {sym(r?.request_type)}
                    </span>
                    {r?.start_time && r?.end_time && (
                      <span className="text-[10px] text-slate-500">{r.start_time}〜{r.end_time}</span>
                    )}
                  </div>
                )}
              </div>
              {/* 配置 */}
              <div className="text-right">
                {isClosed ? (
                  <span className="text-[10px] text-slate-300">—</span>
                ) : s ? (
                  <span className={`text-[11px] px-1.5 py-0.5 rounded border font-semibold ${
                    s.status === "confirmed" ? "text-green-700 bg-green-50 border-green-200" :
                    s.status === "changed" ? "text-yellow-700 bg-yellow-50 border-yellow-200" :
                    "text-blue-700 bg-blue-50 border-blue-200"
                  }`}>
                    ✓ {s.start_time && s.end_time ? `${s.start_time}〜${s.end_time}` : "配置済"}
                  </span>
                ) : (
                  <span className="text-[10px] text-slate-400">—</span>
                )}
              </div>
            </li>
          ))}
        </ul>
        <DialogFooter>
          <Button variant="outline" onClick={onClose} data-testid="day-by-day-close">閉じる</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
